
import { useState, useEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Euro, Search, ArrowLeft } from "lucide-react";

interface EquipmentSummaryItem {
  guardId: string;
  guardName: string;
  totalItems: number;
  totalCost: number;
}

interface GuardEquipmentDetail {
  equipmentId: string;
  equipmentName: string;
  quantity: number;
  unitCost: number;
  totalCost: number;
  assignedAt: string;
  notes: string | null;
}

interface GuardEquipmentSummaryProps {
  refreshTrigger: number;
}

export const GuardEquipmentSummary = ({ refreshTrigger }: GuardEquipmentSummaryProps) => {
  const [summaryData, setSummaryData] = useState<EquipmentSummaryItem[]>([]);
  const [detailedData, setDetailedData] = useState<GuardEquipmentDetail[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGuard, setSelectedGuard] = useState<{ id: string, name: string } | null>(null);
  const { toast } = useToast();
  
  useEffect(() => {
    fetchSummaryData();
  }, [refreshTrigger]);
  
  const fetchSummaryData = async () => {
    try {
      setIsLoading(true);
      
      // Clear selected guard when fetching summary
      setSelectedGuard(null);
      
      // Fixed the query - removed `:1` from is.null filter
      const { data: equipmentAssignments, error: assignmentsError } = await supabase
        .from('guard_equipment')
        .select(`
          id,
          quantity,
          guard_id,
          equipment_type_id,
          profiles:guard_id (full_name),
          equipment_types:equipment_type_id (name, base_price)
        `)
        .eq('status', 'assigned');
        
      if (assignmentsError) throw assignmentsError;
      
      // Process data to get summary by guard
      const guardSummaryMap = new Map<string, { 
        guardId: string;
        guardName: string;
        totalItems: number;
        totalCost: number;
      }>();
      
      equipmentAssignments?.forEach(assignment => {
        if (!assignment.profiles) return; // Skip if profiles is null
        
        const guardId = assignment.guard_id;
        const guardName = typeof assignment.profiles === 'object' && assignment.profiles !== null 
          ? (assignment.profiles as any).full_name || 'Unknown'
          : 'Unknown';
        const quantity = assignment.quantity || 0;
        const unitCost = assignment.equipment_types?.base_price || 0;
        const cost = quantity * unitCost;
        
        if (guardSummaryMap.has(guardId)) {
          const current = guardSummaryMap.get(guardId)!;
          guardSummaryMap.set(guardId, {
            ...current,
            totalItems: current.totalItems + quantity,
            totalCost: current.totalCost + cost
          });
        } else {
          guardSummaryMap.set(guardId, {
            guardId,
            guardName,
            totalItems: quantity,
            totalCost: cost
          });
        }
      });
      
      const summaryArray = Array.from(guardSummaryMap.values());
      setSummaryData(summaryArray);
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load equipment summary: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const fetchGuardDetails = async (guardId: string, guardName: string) => {
    try {
      setIsLoading(true);
      setSelectedGuard({ id: guardId, name: guardName });
      
      // Fixed the query - removed `:1` from is.null filter
      const { data, error } = await supabase
        .from('guard_equipment')
        .select(`
          id,
          quantity,
          assigned_date,
          notes,
          equipment_types:equipment_type_id(id, name, base_price)
        `)
        .eq('guard_id', guardId)
        .eq('status', 'assigned');
        
      if (error) throw error;
      
      const details: GuardEquipmentDetail[] = data.map(item => ({
        equipmentId: item.equipment_types?.id || '',
        equipmentName: item.equipment_types?.name || 'Unknown',
        quantity: item.quantity || 0,
        unitCost: item.equipment_types?.base_price || 0,
        totalCost: (item.quantity || 0) * (item.equipment_types?.base_price || 0),
        assignedAt: new Date(item.assigned_date).toLocaleDateString(),
        notes: item.notes
      }));
      
      setDetailedData(details);
      
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load guard details: ${error.message}`,
        variant: "destructive",
      });
      setSelectedGuard(null);
    } finally {
      setIsLoading(false);
    }
  };
  
  const filteredSummary = summaryData.filter(
    item => item.guardName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleBackToSummary = () => {
    setSelectedGuard(null);
  };
  
  const getTotalCost = () => {
    if (selectedGuard) {
      return detailedData.reduce((acc, item) => acc + item.totalCost, 0);
    }
    return filteredSummary.reduce((acc, item) => acc + item.totalCost, 0);
  };
  
  return (
    <div className="space-y-4">
      {selectedGuard ? (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleBackToSummary}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back
            </Button>
            <h2 className="text-xl font-semibold">{selectedGuard.name}'s Equipment</h2>
          </div>
          
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipment</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Unit Cost</TableHead>
                  <TableHead>Total Cost</TableHead>
                  <TableHead>Assigned On</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      Loading equipment details...
                    </TableCell>
                  </TableRow>
                ) : detailedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No equipment found for this guard.
                    </TableCell>
                  </TableRow>
                ) : (
                  detailedData.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.equipmentName}</TableCell>
                      <TableCell>{item.quantity}</TableCell>
                      <TableCell>€{item.unitCost.toFixed(2)}</TableCell>
                      <TableCell>€{item.totalCost.toFixed(2)}</TableCell>
                      <TableCell>{item.assignedAt}</TableCell>
                      <TableCell>{item.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
                
                {!isLoading && detailedData.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-right font-semibold">Total Cost</TableCell>
                    <TableCell className="font-semibold">€{getTotalCost().toFixed(2)}</TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Guards Equipment Summary</h2>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search guards..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="border rounded-md overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Guard Name</TableHead>
                  <TableHead>Total Items</TableHead>
                  <TableHead>Total Cost</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8">
                      Loading equipment summary...
                    </TableCell>
                  </TableRow>
                ) : filteredSummary.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {searchTerm ? "No guards match your search." : "No guards have equipment assigned."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSummary.map((item) => (
                    <TableRow key={item.guardId}>
                      <TableCell className="font-medium">{item.guardName}</TableCell>
                      <TableCell>{item.totalItems}</TableCell>
                      <TableCell>€{item.totalCost.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => fetchGuardDetails(item.guardId, item.guardName)}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                
                {!isLoading && filteredSummary.length > 0 && (
                  <TableRow>
                    <TableCell className="font-semibold">Total</TableCell>
                    <TableCell className="font-semibold">
                      {filteredSummary.reduce((acc, item) => acc + item.totalItems, 0)}
                    </TableCell>
                    <TableCell className="font-semibold">€{getTotalCost().toFixed(2)}</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
